import React, { createContext, useContext, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import './tones.css';
import './OperationDialog.css';

// Liga o aria-labelledby do diálogo ao título da fase atual, seja o
// cabeçalho do formulário ou o título do OperationStatus.
const TitleIdContext = createContext(undefined);

/**
 * Modal único para uma operação iniciada pelo usuário que depende de um
 * dispositivo/servidor e pode demorar, repetir ou falhar. As fases trocam de
 * conteúdo no mesmo lugar, sem modal empilhado:
 *
 * - 'form': cabeçalho (title + ×) e o formulário em children
 * - 'sending' | 'result': children é um <OperationStatus />
 *
 * Renderizado via portal no body: fica fora de .page-container, cujos estilos
 * genéricos de button/input/select vazariam para dentro do modal.
 *
 * O foco vai, a cada troca de fase, para o elemento marcado com
 * data-autofocus (ou para o próprio painel, quando não há nenhum).
 *
 * @param {boolean} open
 * @param {'form'|'sending'|'result'} phase
 * @param {string} [title] - Título do cabeçalho na fase 'form'
 * @param {Function} [onClose] - Botão × (fase 'form') e tecla Esc; omitir
 *   quando a fase não pode ser interrompida
 */
const OperationDialog = ({ open, phase = 'form', title, onClose, children }) => {
  const panelRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!open || !onClose) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Prende foco, clique e leitor de tela no diálogo: o resto do app (#root)
  // fica inert enquanto ele está aberto — o diálogo vive em portal fora do
  // #root. Ao fechar, o foco volta para quem abriu (ex.: FAB).
  useEffect(() => {
    if (!open) return undefined;
    const appRoot = document.getElementById('root');
    const focoAnterior = document.activeElement;
    appRoot?.setAttribute('inert', '');
    return () => {
      appRoot?.removeAttribute('inert');
      if (focoAnterior instanceof HTMLElement && focoAnterior.isConnected) focoAnterior.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const alvo = panelRef.current.querySelector('[data-autofocus]') || panelRef.current;
    alvo.focus();
  }, [open, phase]);

  if (!open) return null;

  return createPortal(
    <div className="operation-dialog">
      <div
        ref={panelRef}
        className="operation-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-live="polite"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <TitleIdContext.Provider value={titleId}>
          <div key={phase} className="operation-dialog__content">
            {phase === 'form' ? (
              <>
                <div className="operation-dialog__header">
                  <h2 id={titleId} className="operation-dialog__heading">{title}</h2>
                  {onClose && (
                    <button type="button" className="operation-dialog__close" onClick={onClose} aria-label="Fechar">
                      ×
                    </button>
                  )}
                </div>
                {children}
              </>
            ) : children}
          </div>
        </TitleIdContext.Provider>
      </div>
    </div>,
    document.body
  );
};

/**
 * Conteúdo das fases 'sending' e 'result': medalhão de ícone, título, no
 * máximo uma frase, e um botão principal largo com ação secundária em texto.
 *
 * @param {'primary'|'success'|'danger'|'warning'|'info'} tone
 * @param {ReactNode} icon
 * @param {boolean} [loading] - Anel girando em volta do medalhão
 * @param {Array<'ok'|'fail'|'live'|'pending'>} [attempts] - Pontos de tentativa
 * @param {{label: string, onClick: Function, disabled?: boolean}} [primaryAction]
 * @param {{label: string, onClick: Function}} [secondaryAction]
 * @param {number} [autoCloseMs] - Barra de tempo fina no rodapé (o fechamento
 *   em si é responsabilidade de quem usa)
 */
export const OperationStatus = ({
  tone = 'primary',
  icon,
  title,
  description,
  loading = false,
  attempts,
  primaryAction,
  secondaryAction,
  autoCloseMs
}) => {
  const titleId = useContext(TitleIdContext);
  // Principal desabilitado (contagem regressiva) não recebe foco
  const focoNoPrincipal = Boolean(primaryAction && !primaryAction.disabled);

  return (
    <div className={`operation-status feedback-tone--${tone}`}>
      <div
        className={`operation-status__medallion ${loading ? 'operation-status__medallion--loading' : 'operation-status__medallion--settled'}`}
        aria-hidden="true"
      >
        {icon}
      </div>

      <h3 id={titleId} className="operation-status__title">{title}</h3>
      {description && <p className="operation-status__description">{description}</p>}

      {attempts && (
        <div className="operation-status__attempts" aria-hidden="true">
          {attempts.map((estado, i) => (
            <span key={i} className={`operation-status__attempt operation-status__attempt--${estado}`} />
          ))}
        </div>
      )}

      {(primaryAction || secondaryAction) && (
        <div className="operation-status__actions">
          {primaryAction && (
            <button
              type="button"
              className="operation-status__primary"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              data-autofocus={focoNoPrincipal || undefined}
            >
              {primaryAction.label}
            </button>
          )}
          {secondaryAction && (
            <button
              type="button"
              className="operation-status__secondary"
              onClick={secondaryAction.onClick}
              data-autofocus={!focoNoPrincipal || undefined}
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}

      {autoCloseMs > 0 && (
        <div className="operation-status__timer" aria-hidden="true">
          <div className="operation-status__timer-bar" style={{ animationDuration: `${autoCloseMs}ms` }} />
        </div>
      )}
    </div>
  );
};

export default OperationDialog;
